import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  ADD_CREDITS_SUCCESS_MODAL,
  BRANCH_OPTIONS,
  SELECT_BRANCH_MODAL,
  TERMS_AND_CONDITIONS_MODAL,
  VOTED_SUCCESS_MODAL,
} from "../../../constants";
import { scrollIntoView } from "../../../utils/domManipulation";
import { useModal } from "../../../hooks/useModal";
import usePrevious from "../../../hooks/usePrevious";
import {
  useLocal,
  useRadioidByNameseo,
  useTerms,
  useUser,
} from "../../../hooks/useApi";
import { useScrollObserver } from "../../../hooks/useScrollObserver";

import DevPre from "../../dev/DevPre";

import Button from "../../basic/Button";
import Loader from "../../basic/Loader";

import Sticky from "../../layout/Sticky";

import Boxes from "./components/Boxes";
import NowPlaying from "./components/NowPlaying";

import "./styles/sonicbox.scss";
import MenuToggleSVG from "../../../inline-svg/menu-toggle.svg";
import useLanguage, { T } from "../../../hooks/useLanguage";

import PlayIcon from "../../../images/icon-play.png";
import { useLocation, useNavigate } from "@reach/router";
import SearchMusic from "./components/SearchMusic";
import SonicboxHeader from "./components/SonicboxHeader";
import Suggested from "./components/Suggested";
import Artists from "./components/Artists";
import useTheme from "../../../hooks/useTheme";
import { getCookie } from "../../../helpers/cookies";
import { requiresTask } from "../../../helpers/taskGates";
import { reportAccessEvent } from "../../../services/eventEmitter";
import {
  buildRadioMarketingConfig,
  buildTestMarketingConfig,
  isElCorralFansFeatureEnabled,
  MarketingBannerSlot,
  MarketingProvider,
} from "../../../features/marketing";

const Sonicbox = ({ nameseo, termsManaged = false }) => {
  const { setTheme } = useTheme();
  const { i18n } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    if (requiresTask(nameseo)) {
      navigate(`/task/${nameseo}`);
    }
  }, [nameseo, navigate]);

  const { openModal: openSelectBranchModal } = useModal(SELECT_BRANCH_MODAL);
  const { stackModal, lastModal, openModal } = useModal(
    ADD_CREDITS_SUCCESS_MODAL
  );
  const {
    openModal: openTermsModal,
    currentModal,
    closeModal,
  } = useModal(TERMS_AND_CONDITIONS_MODAL);
  const [openPeekTerms, setOpenPeekTerms] = useState(false);
  const { extraQuery, ...termsQuery } = useTerms(nameseo);

  const { user, setRadioVisited } = useUser();
  const { points = 0 } = user ?? {};
  const previousPoints = usePrevious(points);

  const headerRef = useRef();
  const musicRef = useRef();
  const boxesRef = useRef();
  const stickyBoxesRef = useRef();
  const accessReportRef = useRef(null);
  const searchInputRef = useRef();
  const songsWrapperRef = useRef();

  const { ref: songsRef, inView } = useScrollObserver("songs");
  const [selectedParent, setParent] = useState(null);
  const [selectedBox, setSelectedBox] = useState(null);
  const [search, setSearch] = useState("");

  const { radioid, name, marketingConfig } = useRadioidByNameseo(nameseo);

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const isSearch = searchParams.has("s");
  const testMarketingConfig = useMemo(
    () => buildTestMarketingConfig(location.search),
    [location.search]
  );
  const elCorralFansPreviewEnabled = useMemo(
    () => isElCorralFansFeatureEnabled(searchParams),
    [searchParams]
  );

  const {
    isLoading,
    data,
    isError,
    error,
    radioid: currentRadioid,
  } = useLocal(radioid);
  const radioName = data?.name ?? name;
  const radioMarketingConfig = useMemo(
    () =>
      buildRadioMarketingConfig({
        config: marketingConfig,
        radioName,
        bypassFrequency: elCorralFansPreviewEnabled,
      }),
    [elCorralFansPreviewEnabled, marketingConfig, radioName]
  );
  const effectiveMarketingConfig =
    testMarketingConfig ?? radioMarketingConfig;
  const hasHeaderTopMarketing = useMemo(
    () =>
      !!effectiveMarketingConfig?.campaigns?.some((campaign) =>
        campaign.creatives?.some(
          (creative) =>
            creative.format === "banner" &&
            creative.placements?.includes("header_top")
        )
      ),
    [effectiveMarketingConfig]
  );

  const termsAccepted = termsQuery.termsAccepted;

  const marketingBlocked = useMemo(() => {
    if (termsManaged) return false;
    if (termsQuery.isLoading || termsQuery.isTermsAcceptanceLoading) {
      return true;
    }

    return !!termsQuery?.data && !!extraQuery?.data && !termsAccepted;
  }, [
    termsManaged,
    termsAccepted,
    termsQuery.isLoading,
    termsQuery.isTermsAcceptanceLoading,
    termsQuery?.data,
    extraQuery?.data,
  ]);

  useEffect(() => {
    setRadioVisited({
      lastradiovisited: { id: radioid, name: name, nameseo: nameseo },
    });
  }, [radioid, name, nameseo, setRadioVisited]);

  useEffect(() => {
    if (
      requiresTask(nameseo) ||
      !user?.userid ||
      !user?.email ||
      !radioid ||
      !(data?.name || name)
    ) {
      return;
    }

    const accessReportKey = `${user.userid}:${radioid}`;
    if (accessReportRef.current === accessReportKey) {
      return;
    }

    accessReportRef.current = accessReportKey;

    void reportAccessEvent({
      user,
      radio: {
        radioid,
        radioname: data?.name ?? name,
        nameseo,
      },
      payload: {
        pathname: location.pathname,
      },
    }).then((result) => {
      if (result?.status === "skipped_recent_login") {
        accessReportRef.current = null;
      }
    });
  }, [user, radioid, data?.name, name, nameseo, location.pathname]);

  useEffect(() => {
    setTimeout(() => {
      if (
        typeof BRANCH_OPTIONS[nameseo] !== "undefined" &&
        currentModal !== SELECT_BRANCH_MODAL &&
        currentModal !== TERMS_AND_CONDITIONS_MODAL
      ) {
        const branches = BRANCH_OPTIONS[nameseo].branches;
        let chosenBranch = getCookie(nameseo);
        if (Object.keys(branches).length === 1) {
          chosenBranch = Object.keys(branches)[0];
        }
        // console.log(chosenBranch);
        if (Object.keys(branches).indexOf(chosenBranch) === -1) {
          openSelectBranchModal({ nameseo: nameseo });
        } else {
          if (chosenBranch !== nameseo) {
            navigate(`/sonicbox/${chosenBranch}`);
          }
        }
      }
    }, 1000);
  }, [nameseo, openSelectBranchModal, currentModal, navigate]);

  useEffect(() => {
    // console.log("A local currentRadioid", currentRadioid, data);
    if (
      data?.name &&
      (data.name.indexOf("Falabella") > -1 ||
        data.name.indexOf("falabella") > -1)
    ) {
      setTheme("falabella");
    } else if (data?.name && data.name.indexOf("Casper Radio") > -1) {
      setTheme("casper");
    } else {
      setTheme("default");
    }
  }, [setTheme, data, currentRadioid]);

  useEffect(() => {
    if (lastModal === VOTED_SUCCESS_MODAL) {
      // console.log("-------------- VOTED_SUCCESS_MODAL ---------------");
      if (typeof headerRef.current !== "undefined" && headerRef.current) {
        scrollIntoView(headerRef.current);
      }
    }
  }, [lastModal]);

  // useEffect(() => {
  //   if (data?.radiobg && data.radiobg !== "") {
  //     document.body.style.backgroundImage = `url(${data.radiobg})`;
  //   }
  //   return () => (document.body.style.backgroundImage = null);
  // }, [data]);

  //const { isLoading, data, isError, error } = useLocal(radioid, null);

  const handleLetsPlay = () => {
    if (musicRef.current !== undefined) {
      scrollIntoView(musicRef.current, 75);
    }
  };

  const handleOnSelectBox = (selectedBox) => {
    setSearch("");
    setSelectedBox(selectedBox);
    // console.log(
    //   "scrollIntoView songsWrapperRef",
    //   songsWrapperRef,
    //   songsWrapperRef.current
    // );
    navigate(`/sonicbox/${nameseo}?s=&p=${selectedBox}`);
  };

  const handleScrollBack = () => {
    handleLetsPlay();
  };

  const handleOnClickInput = useCallback(
    (event) => {
      event.preventDefault();
      setTimeout(() => {
        if (searchInputRef.current !== undefined) {
          event.target.blur();
          searchInputRef.current.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
          setTimeout(() => {
            searchInputRef.current
              .getElementsByTagName("input")[0]
              .focus({ preventScroll: false });
          }, 500);
          // console.log("focus on other ref", searchInputRef.current);
        }
      }, 500);
      return false;
    },
    [searchInputRef]
  );

  useEffect(() => {
    const pointsInt = parseInt(points);
    const prePointsInt = parseInt(previousPoints);
    if (pointsInt > prePointsInt) {
      stackModal({ credits: pointsInt - prePointsInt });
    }
  }, [points, previousPoints, stackModal]);

  useEffect(() => {
    console.log("extraQuery", extraQuery?.data);
  }, [extraQuery.lastUpdateAt, extraQuery?.data]);

  useEffect(() => {
    // console.log("termsQuery", termsQuery?.data);
    // console.log("extraQuery", extraQuery?.data?.data);
    // console.log("termsAccepted", extraArray, termsAccepted);
    if (
      !termsManaged &&
      termsQuery?.data &&
      extraQuery?.data &&
      !termsQuery.isTermsAcceptanceLoading
    ) {
      console.log(
        "currentModal",
        currentModal,
        TERMS_AND_CONDITIONS_MODAL,
        termsQuery.acceptTerms.status,
        termsAccepted
      );
      if (
        termsQuery.acceptTerms.status === "idle" &&
        !termsAccepted &&
        currentModal !== TERMS_AND_CONDITIONS_MODAL
      ) {
        console.log("openTermsModal", nameseo, currentModal);
        openTermsModal({ nameseo: nameseo });
      }
      if (
        termsAccepted &&
        currentModal === TERMS_AND_CONDITIONS_MODAL &&
        !openPeekTerms
      ) {
        // console.log("now close terms modal");
        closeModal();
      }
    }
  }, [
    termsManaged,
    termsQuery?.data,
    termsQuery?.form,
    nameseo,
    openTermsModal,
    currentModal,
    closeModal,
    openPeekTerms,
    radioid,
    termsQuery.acceptTerms,
    termsQuery.acceptTerms.status,
    extraQuery?.data,
    termsAccepted,
    termsQuery.isTermsAcceptanceLoading,
  ]);

  const selectedPlaylistName = useMemo(() => {
    return data?.playlists?.find(
      (value) => parseInt(value.playlistid) === parseInt(selectedBox)
    )?.playlistname;
  }, [selectedBox, data]);

  const displayName = useMemo(() => {
    if (typeof data?.name === "string" && data?.name.indexOf("Red Bull") > -1) {
      return data.name.replace("Red Bull", "Cúpula");
    }
    return data?.name;
  }, [data?.name]);

  return (
    <div className="container">
      {isError ? (
        <DevPre style={{ color: "red" }}>
          {JSON.stringify(error, null, " ")}
        </DevPre>
      ) : isLoading ? (
        <Loader />
      ) : (
        <MarketingProvider
          config={effectiveMarketingConfig}
          radioid={radioid}
          nameseo={nameseo}
          radioName={radioName}
          blocked={marketingBlocked}
        >
          {isSearch ? (
            <SearchMusic nameseo={nameseo} />
          ) : (
            <div className={["sonicbox"].join(" ")}>
              <NowPlaying {...data} radioid={radioid} />
              <header
                id="header"
                className={hasHeaderTopMarketing ? "has-header-top-marketing" : ""}
                style={{
                  backgroundImage: data?.radiobg ? `url(${data.radiobg})` : "",
                }}
                ref={headerRef}
              >
                <SonicboxHeader {...data} name={displayName} radioid={radioid} />
                <MarketingBannerSlot placement="header_top" />
                <Button
                  className="cta"
                  size={"big"}
                  onClick={handleLetsPlay}
                >
                  <img src={PlayIcon} alt="play" className="icon" />
                  <T>sonicbox.cta</T>
                </Button>
                {termsQuery?.data &&
                  (!!termsQuery?.links ? (
                    <div style={{ display: "flex", justifyContent: "flex-end" }}>
                      {termsQuery.links.map((link, index) => (
                        <Button
                          className="brand"
                          as="link"
                          onClick={() => {
                            window.open(link.href, "_blank");
                          }}
                          key={index}
                          style={{
                            marginLeft: index === 0 ? 0 : 10,
                            opacity: 0.5,
                          }}
                        >
                          {link.textContent}
                        </Button>
                      ))}
                    </div>
                  ) : (
                    <Button
                      className="brand"
                      as="link"
                      onClick={() => {
                        setOpenPeekTerms(true);
                        openTermsModal({
                          nameseo: nameseo,
                          isPeeking: true,
                          setIsPeeking: setOpenPeekTerms,
                        });
                      }}
                      style={{
                        marginLeft: "auto",
                        display: "block",
                        marginTop: 20,
                        marginBottom: -14,
                        marginRight: -8,
                        fontSize: 12,
                        opacity: 0.5,
                      }}
                    >
                      <T>sonicbox.terms</T>
                    </Button>
                  ))}
              </header>
              <MarketingBannerSlot placement="after_header" />
              <div id="boxes" ref={musicRef}>
                <Boxes
                  ref={boxesRef}
                  playlists={
                    data
                      ? data.playlists.sort((playlist) =>
                        playlist.playlistid === "737" ? -1 : 1
                      )
                      : []
                  }
                  parents={data ? data.parents : []}
                  onSelectBox={handleOnSelectBox}
                  selectedBox={selectedBox}
                  boxesRef={boxesRef}
                  songsRef={songsRef}
                  boxesOffset={80}
                  songsOffset={180}
                  selectedParent={selectedParent}
                  setParent={setParent}
                  title={
                    data ? <T name={displayName}>sonicbox.boxestitle</T> : ""
                  }
                />
              </div>
              <MarketingBannerSlot placement="after_boxes" />
              <div id="suggested">
                <Suggested radio={data} />
              </div>
              <MarketingBannerSlot placement="between_suggested_artists" />
              <div id="artists">
                <Artists radio={data} />
              </div>
            </div>
          )}
        </MarketingProvider>
      )}
      <Button
        className={["blue", "scroll-back-button", inView ? "show" : ""].join(
          " "
        )}
        onClick={handleScrollBack}
      >
        <img src={MenuToggleSVG} alt="ir arriba" />
      </Button>
    </div>
  );
};

export default Sonicbox;
